/**
 * Sonidos de la partida.
 *
 * Se sintetizan con la Web Audio API en vez de cargar archivos: así no hay
 * ni una petición de red, el primer sonido suena sin retardo y el conjunto
 * pesa cero. El volumen general es configurable desde las preferencias.
 */

class SoundManager {
  private ctx: AudioContext | null = null;
  private soundEnabled = true;
  private hapticsEnabled = true;

  constructor() {
    // AudioContext will be initialized on first user gesture
  }

  private getAudioContext(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!this.ctx) {
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (AudioCtx) {
        this.ctx = new AudioCtx();
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    return this.ctx;
  }

  public setSoundEnabled(enabled: boolean) {
    this.soundEnabled = enabled;
  }

  public isSoundEnabled(): boolean {
    return this.soundEnabled;
  }

  public setHapticsEnabled(enabled: boolean) {
    this.hapticsEnabled = enabled;
  }

  public isHapticsEnabled(): boolean {
    return this.hapticsEnabled;
  }

  public triggerHaptic(type: 'light' | 'medium' | 'heavy' | 'success' | 'error' = 'light') {
    if (!this.hapticsEnabled || typeof navigator === 'undefined' || !navigator.vibrate) return;
    try {
      if (type === 'light') navigator.vibrate(12);
      else if (type === 'medium') navigator.vibrate(25);
      else if (type === 'heavy') navigator.vibrate(45);
      else if (type === 'success') navigator.vibrate([20, 40, 30]);
      else if (type === 'error') navigator.vibrate([40, 60, 40]);
    } catch {
      // ignore
    }
  }

  /** El clásico «toc» de pieza de madera apoyada sobre el tablero. */
  public playMove() {
    this.triggerHaptic('light');
    if (!this.soundEnabled) return;
    this.woodClick({ pitch: 1, level: 0.9 });
  }

  /**
   * Captura: la pieza golpea a la capturada y luego apoya, así que son dos
   * golpes muy seguidos, el primero más agudo y seco.
   */
  public playCapture() {
    this.triggerHaptic('medium');
    if (!this.soundEnabled) return;
    this.woodClick({ pitch: 1.3, level: 0.75 });
    this.woodClick({ pitch: 0.95, level: 1, delay: 0.035 });
  }

  /** Ruido blanco reutilizable, la materia prima del golpe de madera. */
  private noise: AudioBuffer | null = null;

  private noiseBuffer(ctx: AudioContext): AudioBuffer {
    if (this.noise && this.noise.sampleRate === ctx.sampleRate) return this.noise;
    const length = Math.floor(ctx.sampleRate * 0.1);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    this.noise = buffer;
    return buffer;
  }

  /**
   * Golpe de madera: un chasquido de ruido muy corto que hacen sonar dos
   * resonancias (la de la pieza, aguda, y la del tablero, media), más un
   * golpe grave casi imperceptible que le da cuerpo.
   */
  private woodClick({ pitch, level, delay = 0 }: { pitch: number; level: number; delay?: number }) {
    const ctx = this.getAudioContext();
    if (!ctx) return;

    const start = ctx.currentTime + delay;
    const peak = level * this.volume;

    const out = ctx.createGain();
    out.gain.value = peak;
    out.connect(ctx.destination);

    // Chasquido: ruido con ataque instantáneo y caída muy rápida.
    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer(ctx);
    const envelope = ctx.createGain();
    envelope.gain.setValueAtTime(0.0001, start);
    envelope.gain.exponentialRampToValueAtTime(1, start + 0.001);
    envelope.gain.exponentialRampToValueAtTime(0.0001, start + 0.045);
    source.connect(envelope);

    const resonances: Array<[frequency: number, q: number, gain: number]> = [
      [2000 * pitch, 7, 1.6], // la pieza
      [850 * pitch, 5, 1.1], // el tablero
    ];
    for (const [frequency, q, gain] of resonances) {
      const band = ctx.createBiquadFilter();
      band.type = 'bandpass';
      band.frequency.value = frequency;
      band.Q.value = q;
      const amount = ctx.createGain();
      amount.gain.value = gain;
      envelope.connect(band);
      band.connect(amount);
      amount.connect(out);
    }

    // Cuerpo: tono grave que cae enseguida.
    const body = ctx.createOscillator();
    const bodyGain = ctx.createGain();
    body.type = 'sine';
    body.frequency.setValueAtTime(230 * pitch, start);
    body.frequency.exponentialRampToValueAtTime(120 * pitch, start + 0.05);
    bodyGain.gain.setValueAtTime(0.35, start);
    bodyGain.gain.exponentialRampToValueAtTime(0.0001, start + 0.06);
    body.connect(bodyGain);
    bodyGain.connect(out);

    source.start(start);
    source.stop(start + 0.08);
    body.start(start);
    body.stop(start + 0.07);
  }

  // Check alert sound
  public playCheck() {
    this.triggerHaptic('heavy');
    if (!this.soundEnabled) return;
    const ctx = this.getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(587.33, now); // D5
    osc.frequency.setValueAtTime(880, now + 0.08); // A5

    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.26);
  }

  // Castling shuffle sound
  public playCastle() {
    this.playMove();
    setTimeout(() => {
      this.playMove();
    }, 90);
  }

  // Promotion chord
  public playPromote() {
    this.triggerHaptic('success');
    if (!this.soundEnabled) return;
    const ctx = this.getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
    notes.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const startTime = now + idx * 0.06;

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, startTime);
      gain.gain.setValueAtTime(0.2, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.2);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(startTime);
      osc.stop(startTime + 0.22);
    });
  }

  // Game victory fanfare
  public playVictory() {
    this.triggerHaptic('success');
    if (!this.soundEnabled) return;
    const ctx = this.getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.5, 1318.51];
    notes.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const startTime = now + idx * 0.08;

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, startTime);
      gain.gain.setValueAtTime(0.25, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.4);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(startTime);
      osc.stop(startTime + 0.45);
    });
  }

  // Game defeat sound
  public playDefeat() {
    this.triggerHaptic('error');
    if (!this.soundEnabled) return;
    const ctx = this.getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const notes = [440, 392, 349.23, 293.66];
    notes.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const startTime = now + idx * 0.12;

      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq, startTime);
      gain.gain.setValueAtTime(0.18, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.3);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(startTime);
      osc.stop(startTime + 0.35);
    });
  }

  // Low time warning tick
  public playLowTimeTick() {
    this.triggerHaptic('light');
    if (!this.soundEnabled) return;
    const ctx = this.getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(900, now);
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.04);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.05);
  }

  // Puzzle success sound
  public playPuzzleSuccess() {
    this.playPromote();
  }
  /** Volumen general, de 0 a 1. */
  private volume = 0.7;

  public setVolume(value: number) {
    this.volume = Math.min(1, Math.max(0, value));
  }

  /** Tono breve y neutro al elegir una pieza. */
  public playSelect() {
    if (!this.soundEnabled) return;
    this.blip(320, 0.03, 0.05);
  }

  /** Aviso seco de jugada no permitida. */
  public playIllegal() {
    if (!this.soundEnabled) return;
    this.blip(150, 0.09, 0.09, 'square');
    this.triggerHaptic('error');
  }

  /** Confirmación sorda al dejar preparado un premovimiento. */
  public playPremove() {
    if (!this.soundEnabled) return;
    this.blip(420, 0.04, 0.06, 'triangle');
  }

  /** Campanada de inicio de partida. */
  public playGameStart() {
    if (!this.soundEnabled) return;
    this.blip(523.25, 0.12, 0.10);
    setTimeout(() => this.blip(783.99, 0.16, 0.10), 110);
  }

  /** Aviso de notificación entrante. */
  public playNotify() {
    if (!this.soundEnabled) return;
    this.blip(880, 0.08, 0.07);
    setTimeout(() => this.blip(1174.66, 0.10, 0.06), 90);
  }

  /** Bloque elemental del que se componen los avisos cortos. */
  private blip(
    frequency: number,
    duration: number,
    gainValue: number,
    type: OscillatorType = 'sine',
  ) {
    const ctx = this.getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(frequency, now);
    gain.gain.setValueAtTime(gainValue * this.volume, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + duration + 0.01);
  }
}

export const sound = new SoundManager();
export const soundManager = sound;
